/**
 * GraphQL client for the Movatic API
 *
 * This module provides a configured GraphQL client using graphql-request
 * for use with React Query hooks.
 */

import { ClientError, GraphQLClient, ResponseMiddleware } from 'graphql-request';
import { getCredentials } from '../http';
import { getGraphQLEndpoint } from './endpoints';

/**
 * Response middleware that handles 401 unauthorized responses.
 * graphql-request surfaces non-2xx responses as a ClientError (with the
 * status on its inner response), not as a fetch Response.
 */
const unauthorizedMiddleware: ResponseMiddleware = (response) => {
  let status: number | undefined;
  if (response instanceof ClientError) {
    status = response.response.status;
  } else if (!(response instanceof Error)) {
    status = response.status;
  }

  if (status === 401) {
    window.dispatchEvent(new CustomEvent('api:unauthorized'));
  }
};

/**
 * Create the GraphQL client with authentication
 */
function createGraphQLClient(): GraphQLClient {
  const client = new GraphQLClient(getGraphQLEndpoint(), {
    responseMiddleware: unauthorizedMiddleware,
  });

  return client;
}

/**
 * Get a configured GraphQL client with current auth headers
 */
export function getGraphQLClient(): GraphQLClient {
  const client = createGraphQLClient();
  const { graphqlToken } = getCredentials();

  if (graphqlToken) {
    client.setHeader('Authorization', `Bearer ${graphqlToken}`);
  }

  return client;
}

/**
 * Export a default client instance
 * Note: For requests that need fresh auth, use getGraphQLClient() instead
 */
export const graphqlClient = createGraphQLClient();

export { getGraphQLEndpoint };
